/**
 * Where a signed-OUT worker's task link is remembered, so signing in sends them back to it
 * (SCRUM-762) — the same one deliberate exception to «no return trip» that `offer-return-intent.ts`
 * makes for a «Make offer» click, and `localStorage` for the same reason: the round trip can cross a
 * tab (a password manager, the sign-in letter opened from the mail app). Never throws.
 */
const KEY = "scb.crewReturnIntent";
/** The ROLE the person chose before signing in (SCRUM-760 S2: «Sign in to take» remembers the role, brief §8), beside the token. */
const CHOICE_KEY = "scb.crewReturnChoice";

/** What a person chose on the link's page: a role (with the rental's own role), or the place a letter named. */
export interface CrewReturnChoice {
  role?: "crew" | "driver";
  crewRoleId?: string | null;
  slot?: string;
}

export function saveCrewReturnIntent(token: string, choice?: CrewReturnChoice): void {
  try {
    localStorage.setItem(KEY, token);
    if (choice) localStorage.setItem(CHOICE_KEY, JSON.stringify({ token, choice }));
    else localStorage.removeItem(CHOICE_KEY);
  } catch {
    /* the link still reaches /login, it just loses the return trip */
  }
}

export function getCrewReturnIntent(): string | undefined {
  try {
    const token = localStorage.getItem(KEY);
    return token && token.length <= 64 ? token : undefined;
  } catch {
    return undefined;
  }
}

/** The role remembered for `token`, if the person chose one before signing in; never another link's. Never throws. */
export function getCrewReturnChoice(token: string): CrewReturnChoice | undefined {
  try {
    const raw = localStorage.getItem(CHOICE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { token?: unknown; choice?: unknown };
    if (parsed.token !== token || typeof parsed.choice !== "object" || parsed.choice === null) return undefined;
    const c = parsed.choice as Record<string, unknown>;
    const choice: CrewReturnChoice = {};
    if (c.role === "crew" || c.role === "driver") choice.role = c.role;
    if (typeof c.crewRoleId === "string") choice.crewRoleId = c.crewRoleId;
    if (typeof c.slot === "string") choice.slot = c.slot;
    return Object.keys(choice).length > 0 ? choice : undefined;
  } catch {
    return undefined;
  }
}

/** Clears the remembered link only if it is `token` — opening another link must not lose the one still waiting. */
export function clearCrewReturnIntent(token: string): void {
  try {
    if (localStorage.getItem(KEY) === token) localStorage.removeItem(KEY);
    if (getCrewReturnChoice(token)) localStorage.removeItem(CHOICE_KEY);
  } catch {
    /* nothing to clean */
  }
}
